import { TourxLogo } from '../components/TourxLogo';

export function MarketingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-black">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <TourxLogo height={32} />
          </a>
          <div className="flex items-center gap-6">
            <a href="/support" className="text-sm text-gray-400 hover:text-white transition-colors">Support</a>
            <a href="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy</a>
            <a
              href="/login"
              className="text-sm bg-white text-black px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Sign In
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 text-center">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
            Transport compliance,<br />simplified.
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            TOURX is the all-in-one platform for transport operators. Tachograph analysis, fleet management,
            DVSA-compliant walkaround checks, driver compliance tracking, and a mobile app for drivers on the road.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/login"
              className="bg-white text-black px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            >
              Get Started
            </a>
            <a
              href="mailto:info@tourx.co.uk"
              className="border border-gray-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:border-gray-400 transition-colors"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">Everything you need to stay compliant</h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Built for UK transport operators. TOURX brings together tachograph analysis, fleet management,
            driver compliance, and daily walkaround checks in one platform.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Tachograph Analysis</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Upload .ddd files and get instant infringement detection. EU Regulation 561/2006 and Working Time Directive
                compliance analysis with severity scoring and automated driver notification letters.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Walkaround Checks</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                DVSA-compliant 50-point daily vehicle inspections via the mobile app. Drivers can attach photo evidence,
                flag defects, and submit checks — even offline. Transport managers are notified of issues instantly.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Driver Compliance (DCRS)</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Points-based Driver Compliance Rating System. Automatically tracks infringements, assigns severity levels,
                triggers procedural actions, and generates formal notification letters — all audit-ready.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fleet Management</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Track MOT dates, tax expiry, service schedules, and insurance for every vehicle. Dashboard alerts warn you
                before anything lapses. Full vehicle history at your fingertips.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Driver Mobile App</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Purpose-built iOS app for drivers. PIN login for quick access, walkaround checks with camera integration,
                POD signature capture, and full offline support. Syncs automatically when back online.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Live Vehicle Tracking</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Real-time GPS tracking of your fleet. See vehicle locations on a live map, monitor routes,
                and keep your operations running smoothly with full visibility across your vehicles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Built for UK transport operators</h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-black text-gray-900 mb-2">HGV</div>
              <p className="text-gray-600">Fleets of all sizes — from single-vehicle operators to large logistics companies.</p>
            </div>
            <div>
              <div className="text-4xl font-black text-gray-900 mb-2">PSV</div>
              <p className="text-gray-600">Coach and bus operators managing driver hours, vehicle checks, and compliance.</p>
            </div>
            <div>
              <div className="text-4xl font-black text-gray-900 mb-2">LGV</div>
              <p className="text-gray-600">Light goods operators staying on top of working time and vehicle maintenance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to simplify your compliance?</h2>
          <p className="text-gray-400 mb-8">
            Join transport operators across the UK who trust TOURX to manage their fleet compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@tourx.co.uk"
              className="bg-white text-black px-8 py-3 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </a>
            <a
              href="/support"
              className="border border-gray-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:border-gray-400 transition-colors"
            >
              Support
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="/privacy" className="hover:text-gray-300 transition-colors">Privacy Policy</a>
              <a href="/support" className="hover:text-gray-300 transition-colors">Support</a>
              <a href="mailto:info@tourx.co.uk" className="hover:text-gray-300 transition-colors">info@tourx.co.uk</a>
            </div>
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} Craner Kirkman Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
